import { DataRefreshEvent } from "@fp/admin-api-client";
import { listTalentMembers } from "@fp/admin-api-client/client";
import { listPayeeOrganizations } from "@fp/admin-api-client/payee-organizations/payee-organizations.client";
import {
	getSettings,
	useUpdateSettings,
} from "@fp/admin-api-client/settings/settings.client";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { TalentMemberList, type RosterFilterSettings } from "@fp/ui/components/talent-members/talent-member-list";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useDataRefresh } from "@/hooks/useSignalR";

export const Route = createFileRoute("/roster/")({
	loader: async () => {
		const [talentMembersResponse, payeeOrganizationsResponse, settingsResponse] = await Promise.all([
			listTalentMembers({ page: 1, pageSize: 1000 }),
			listPayeeOrganizations(),
			getSettings().catch(() => null),
		]);
		return {
			talentMembers: talentMembersResponse.data.items ?? [],
			payeeOrganizations: payeeOrganizationsResponse.data,
			filterSettings: settingsResponse?.data?.roster?.filter ?? undefined,
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const data = Route.useLoaderData();

	const router = useRouter();

	const updateSettingsMutation = useUpdateSettings();

	const invalidateRoster = useCallback(() => { router.invalidate(); }, [router]);
	useDataRefresh(DataRefreshEvent.InvitationCreated, invalidateRoster);
	useDataRefresh(DataRefreshEvent.InvitationAccepted, invalidateRoster);
	useDataRefresh(DataRefreshEvent.JoinRequestApproved, invalidateRoster);
	useDataRefresh(DataRefreshEvent.TalentMemberJoined, invalidateRoster);

	const onTalentMemberNavigate = useCallback(
		(talentMemberId: string) => {
			router.navigate({
				to: "/roster/$id/edit",
				params: { id: talentMemberId },
			});
		},
		[router],
	);

	const onPayeeOrganizationNavigate = useCallback(
		(payeeOrganizationId: string) => {
			router.navigate({
				to: "/roster/organizations/$id",
				params: { id: payeeOrganizationId },
			});
		},
		[router],
	);

	const onNewPayee = () => {
		router.navigate({ to: "/roster/add" });
	};

	const handleFilterSettingsChange = useCallback(
		(settings: RosterFilterSettings) => {
			updateSettingsMutation.mutate({ data: { roster: { filter: settings } } });
		},
		[updateSettingsMutation],
	);

	return (
		<AdminLayout>
			<AdminPageHeader title="Roster" />
			<PageContentContainer className="space-y-6 bg-white p-0">
				<TalentMemberList
					talentMembers={data.talentMembers}
					payeeOrganizations={data.payeeOrganizations}
					onTalentMemberNavigate={onTalentMemberNavigate}
					onPayeeOrganizationNavigate={onPayeeOrganizationNavigate}
					onNewTalentMember={onNewPayee}
					onTalentMemberUpdate={async () => {
						router.invalidate();
					}}
					initialFilterSettings={data.filterSettings}
					onFilterSettingsChange={handleFilterSettingsChange}
				/>
			</PageContentContainer>
		</AdminLayout>
	);
}
